import React, { useState } from 'react';
import {
  Users, Package, Calculator, Database, Loader2, User, LogOut, Settings,
  ChevronDown, ChevronRight, CreditCard, BarChart3, Building2, FolderTree
} from 'lucide-react';
import { DataCategory, SAPMainCategory } from '../../types';
import { useSAPCategories } from '../../hooks/useSAPCategories';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import { isSuperAdmin, isAdmin } from '../../utils/roles';

interface SidebarProps {
  selectedCategory: DataCategory;
  onCategoryChange: (category: DataCategory) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const getCategoryIcon = (categoryName: string) => {
  const name = categoryName.toLowerCase();
  if (name.includes('business partner') || name.includes('partner')) return Users;
  if (name.includes('item') || name.includes('product')) return Package;
  if (name.includes('financial') || name.includes('accounting')) return Calculator;
  return Database;
};

const Sidebar: React.FC<SidebarProps> = ({ selectedCategory, onCategoryChange, isCollapsed = false, onToggleCollapse }) => {
  const { categories, loading, error } = useSAPCategories();
  const { user, logout, hasPermission } = useAuth();
  const { activeOrg, organizations, switchOrg } = useOrg();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [expandedSettings, setExpandedSettings] = useState(false);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);

  const userIsSuperAdmin = isSuperAdmin(user?.user_role) || isSuperAdmin(user?.role_name);
  const userIsAdmin = isAdmin(user?.user_role) || isAdmin(user?.role_name);

  const menuItems = categories
    .map((category: SAPMainCategory) => ({
      id: category.MainCategoryName.replace(/\s+/g, '') as DataCategory,
      label: category.MainCategoryName,
      icon: getCategoryIcon(category.MainCategoryName),
      moduleName: category.MainCategoryName,
    }))
    .filter(item => hasPermission(item.moduleName, 'can_read'));

  const settingsMenuItems = [
    { id: 'profile', label: 'Profile Settings', icon: User, moduleName: 'Profile Settings' },
    { id: 'users', label: 'Users', icon: Users, moduleName: 'Users', adminOnly: true },
    { id: 'roles-departments', label: 'Roles & Departments', icon: Database, moduleName: 'Roles & Departments', adminOnly: true },
    { id: 'authorization', label: 'Authorization', icon: Settings, moduleName: 'Authorization', adminOnly: true },
    { id: 'categories', label: 'Categories', icon: FolderTree, moduleName: 'Categories', adminOnly: true },
    ...(userIsSuperAdmin ? [{ id: 'organizations', label: 'Organizations', icon: Building2, moduleName: 'Organization Management', adminOnly: false }] : []),
    { id: 'billing', label: 'Billing', icon: CreditCard, moduleName: 'Billing & Plans' },
    { id: 'usage-analytics', label: 'Usage Analytics', icon: BarChart3, moduleName: 'Usage Analytics', adminOnly: true },
  ].filter(item => {
    if ((item as any).adminOnly) return userIsAdmin;
    return hasPermission(item.moduleName, 'can_read');
  });

  const isSettingsSubmenuActive = settingsMenuItems.some(item => selectedCategory === item.id);

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-80'} bg-white border-r border-gray-200 flex flex-col shadow-sm min-h-screen transition-all duration-300`}>
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-200">
        <div className="flex items-center justify-center">
          {!isCollapsed && (
            <img src="/images/SAP_BusinessOne_R_grad_blu.png" alt="SAP Business One" className="h-8 w-auto object-contain" />
          )}
        </div>
      </div>

      {/* Org Switcher — super_admin only */}
      {userIsSuperAdmin && !isCollapsed && organizations.length > 0 && (
        <div className="px-4 py-2 border-b border-gray-100">
          <div className="relative">
            <button
              onClick={() => setShowOrgDropdown(!showOrgDropdown)}
              className="w-full flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-left hover:bg-blue-100 transition-colors"
            >
              <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-blue-800 truncate">{activeOrg?.name || 'Select Organization'}</div>
                <div className="text-[10px] text-blue-500 truncate">{activeOrg?.id?.substring(0, 20)}...</div>
              </div>
              <ChevronDown className="w-3 h-3 text-blue-400 flex-shrink-0" />
            </button>

            {showOrgDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 max-h-60 overflow-auto">
                {organizations.map((org: any) => (
                  <button
                    key={org.id}
                    onClick={() => { switchOrg(org.id); setShowOrgDropdown(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2 ${activeOrg?.id === org.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                  >
                    <Building2 className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{org.name}</span>
                    {activeOrg?.id === org.id && <span className="ml-auto text-[10px] text-blue-500">Active</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
            {!isCollapsed && <span className="ml-2 text-gray-600">Loading...</span>}
          </div>
        ) : error ? (
          <div className="text-red-500 text-center py-8">
            <p className="text-sm">Failed to load categories</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = selectedCategory === item.id;
              return (
                <li key={item.id}>
                  <button onClick={() => onCategoryChange(item.id)}
                    className={`w-full flex items-center px-3 py-2.5 rounded-md text-left transition-all duration-200 group ${isActive ? 'menu-item-active' : 'menu-item-inactive'}`}
                    title={isCollapsed ? item.label : undefined}>
                    <Icon className={`w-4 h-4 ${isCollapsed ? 'mx-auto' : 'mr-3'} flex-shrink-0 ${isActive ? 'menu-item-active-icon' : 'menu-item-inactive-icon'}`} />
                    {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </button>
                </li>
              );
            })}

            {/* Settings */}
            <li className="pt-2">
              <div className="border-t border-gray-200 pt-2">
                <button onClick={() => setExpandedSettings(!expandedSettings)}
                  className={`w-full flex items-center px-3 py-2.5 rounded-md text-left transition-all duration-200 group ${isSettingsSubmenuActive ? 'menu-item-active' : 'menu-item-inactive'}`}
                  title={isCollapsed ? 'Settings' : undefined}>
                  <Settings className={`w-4 h-4 ${isCollapsed ? 'mx-auto' : 'mr-3'} flex-shrink-0 ${isSettingsSubmenuActive ? 'menu-item-active-icon' : 'menu-item-inactive-icon'}`} />
                  {!isCollapsed && (
                    <>
                      <span className="text-sm font-medium">Settings</span>
                      <div className="ml-auto">
                        {expandedSettings ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                      </div>
                    </>
                  )}
                </button>

                {!isCollapsed && expandedSettings && (
                  <ul className="ml-4 mt-1 space-y-1">
                    {settingsMenuItems.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const isSubActive = selectedCategory === subItem.id;
                      return (
                        <li key={subItem.id}>
                          <button onClick={() => onCategoryChange(subItem.id as DataCategory)}
                            className={`w-full flex items-center px-3 py-2 rounded-md text-left transition-all duration-200 group ${isSubActive ? 'menu-subitem-active' : 'menu-subitem-inactive'}`}>
                            <SubIcon className={`w-3 h-3 mr-3 flex-shrink-0 ${isSubActive ? 'menu-item-active-icon' : 'menu-item-inactive-icon'}`} />
                            <span className="text-xs font-medium">{subItem.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </li>
          </ul>
        )}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-gray-200">
        {user && (
          <div className="relative">
            <div className="flex items-center space-x-2">
              <button onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex-1 flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                title={isCollapsed ? `${user.user_name} (${user.user_email})` : undefined}>
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                {!isCollapsed && (
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium text-gray-900">{user.user_name}</div>
                    <div className="text-xs text-gray-500">{user.user_email}</div>
                  </div>
                )}
              </button>
              {onToggleCollapse && (
                <button onClick={onToggleCollapse} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors" title={isCollapsed ? "Expand" : "Collapse"}>
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isCollapsed ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />}
                  </svg>
                </button>
              )}
            </div>

            {showUserMenu && !isCollapsed && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <div className="text-sm font-medium text-gray-900">{user.user_name}</div>
                  <div className="text-xs text-gray-500">{user.user_role}</div>
                </div>
                <button onClick={() => { setShowUserMenu(false); logout(); }}
                  className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
